import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  delay,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { LoginSuccess, Logout } from 'src/app/auth/store/auth.actions';
import { IAuthTable } from 'src/app/auth/store/auth.interfaces';
import { StrataPlanRequest } from 'src/app/shared/models/action-request';
import { StrataResponse } from 'src/app/shared/models/strata-response';
import { UserLogin } from 'src/app/shared/services/roles.service';
import { environment } from 'src/environments/environment';
import { ResidentProfileHttpService } from './../../../shared/services/resident-profile-http.service';
import { SetUnitId } from './../lot/lot.actions';
import {
  FetchLotsAndUnits,
  FetchLotsAndUnitsFailure,
  FetchLotsAndUnitsSuccess,
  FetchLotsAndUnitsWithStrataPlan,
  FetchLotsAndUnitsWithStrataPlanSuccess,
  FetchSiteSettings,
  FetchSiteSettingsFailure,
  FetchSiteSettingsSuccess,
  UIActionTypes,
} from './../ui/ui.actions';
import {
  ChangeProfile,
  LoadResidentProfile,
  LoadResidentProfileSuccess,
  ResidentProfileActionTypes,
  SetCurrentResidentProfileId,
  SetCurrentResidentRoleId,
  UpdateResidentProfile,
  UpdateResidentProfileFailure,
  UpdateResidentProfileSuccess,
} from './resident-profile.actions';
import { IResidentProfileCommon } from './resident-profile.interfaces';

@Injectable()
export class ResidentProfileEffects {
  loadResidentProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadResidentProfile>(
        ResidentProfileActionTypes.LOAD_RESIDENT_PROFILE
      ),
      delay(500),
      switchMap(() => {
        return this.apiService
          .loadUserProfile()
          .pipe(
            concatMap((profile: IResidentProfileCommon) => [
              new SetCurrentResidentProfileId(profile.profileId),
              new SetCurrentResidentRoleId(profile.roleId),
              new LoadResidentProfileSuccess(profile),
              new SetUnitId(profile.unitId),
            ])
          );
      })
    )
  );

  fetchLotsAndUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchLotsAndUnits>(UIActionTypes.FETCH_LOTS_AND_UNITS),
      delay(500),
      switchMap((action) => this.apiService.fetchLotsAndUnits(action.payload)),
      concatMap((strataResponse: StrataResponse) => [
        new FetchLotsAndUnitsSuccess(strataResponse),
        new FetchSiteSettings(strataResponse),
      ])
    )
  );

  fetchLotsAndUnitsWithStrata$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchLotsAndUnitsWithStrataPlan>(
        UIActionTypes.FETCH_LOTS_AND_UNITS_WITH_STRATA_PLAN
      ),
      delay(500),
      switchMap(() => this.apiService.fetchLotsAndUnitsWithStrataPlan()),
      map((strataResponse: StrataResponse) => {
        localStorage.setItem(
          environment.ui.strata_plan_friendly_name,
          strataResponse.strataPlanNumberFriendlyName
        );
        localStorage.setItem(
          environment.ui.body_corporate_id,
          strataResponse.bodyCorporateId.toString()
        );
        return strataResponse;
      }),
      concatMap((strataResponse: StrataResponse) => [
        new FetchLotsAndUnitsWithStrataPlanSuccess(strataResponse),
        new FetchSiteSettings(strataResponse),
      ])
    )
  );

  fetchSiteSettingsSucces$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchSiteSettings>(UIActionTypes.FETCH_SITE_SETTINGS),
      delay(500),
      switchMap((action) => {
        if (action.payload !== null) {
          return this.apiService
            .getSiteSettings(
              new StrataPlanRequest(action.payload.strataPlanNumber)
            )
            .pipe(
              map((res) => new FetchSiteSettingsSuccess(res)),
              catchError((error) => of(new FetchSiteSettingsFailure(error)))
            );
        }
        localStorage.removeItem(environment.ui.strata_plan_friendly_name);
        this.router.navigate([environment.auth.not_found]);
        // this causes browser to crash. Need to investigate why. But it still works, even if it would be ideal to dispatch this
        // return of(new FetchSiteSettingsFailure('Incorrect Strata Plan Number'));
      })
    )
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateResidentProfile>(
        ResidentProfileActionTypes.UPDATE_RESIDENT_PROFILE
      ),
      delay(500),
      switchMap((action) => {
        return this.apiService.updateProfile(action.payload).pipe(
          map(
            (res) =>
              new UpdateResidentProfileSuccess({
                profile: action.payload,
                image: res,
              })
          ), // can use response too
          catchError((error) => of(new UpdateResidentProfileFailure(error)))
        );
      })
    )
  );

  updateProfileError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateResidentProfileFailure>(
          ResidentProfileActionTypes.UPDATE_RESIDENT_PROFILE_FAILURE
        ),
        delay(500),
        tap((err: any) => {
          console.log(JSON.stringify(err));
        })
      ),
    { dispatch: false }
  );

  fetchError$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchLotsAndUnitsFailure>(
        UIActionTypes.FETCH_LOTS_AND_UNITS_FAILURE
      ),
      delay(500),
      tap(() => {
        localStorage.removeItem(environment.ui.strata_plan_friendly_name);
        localStorage.removeItem(environment.ui.sub_domain);
        return new Logout();
      })
    )
  );

  fetchSiteSettingsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<FetchLotsAndUnitsFailure>(
          UIActionTypes.FETCH_SITE_SETTINGS_FAILURE
        ),
        delay(500),
        tap((err) => {
          console.log(JSON.stringify(err));
        })
      ),
    { dispatch: false }
  );

  changeProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ChangeProfile>(ResidentProfileActionTypes.CHANGE_PROFILE),
      delay(500),
      switchMap((event) => this.apiService.changeProfile(event.profileId)),
      map((authResponse) => {
        localStorage.setItem(environment.auth.token, authResponse.accessToken);
        localStorage.setItem(
          environment.auth.token_expiry,
          authResponse.expiresIn.toString()
        );
        localStorage.setItem(
          environment.auth.refresh_token,
          authResponse.refreshToken
        );

        let authState: IAuthTable = {
          entities: {
            '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
              accessToken: authResponse.accessToken,
              expiresIn: authResponse.expiresIn,
              refreshToken: authResponse.refreshToken,
              loggedIn: true,
            },
          },
          ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
        };
        localStorage.setItem(environment.auth.user_login, UserLogin.Admin);
        return new LoginSuccess(authState);
      })
    )
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private apiService: ResidentProfileHttpService
  ) {}
}
